<template>
  <t-dropdown :show="isShownOptions" :classes="dropdownClasses" class="columns-settings-dropdown">
    <div slot="trigger" class="relative" @mousedown.stop="onMousedown">
      <OurButton variant="thirdary" size="sm" filled :data-cy="`${dataCy}-button`">
        <template #left>
          <OurSvgIcon :src="icons.Settings" />
        </template>
      </OurButton>
    </div>

    <div class="dropdown-content" @click.stop>
      <div class="dropdown-title">{{ title || $t("button.settings") }}</div>
      <draggable
        v-model="orderedHeaders"
        class="headers-list"
        handle=".drag-icon"
        drag-class="draggable-item"
        :animation="200"
        ghost-class="ghost"
      >
        <div
          v-for="header in orderedHeaders"
          :key="header.id"
          class="header-option-row"
          :data-cy="`${dataCy}-row-${header.id}`"
        >
          <OurSvgIcon
            class="drag-icon"
            :src="icons.DialNumbers"
            color="gray"
            variant="light"
            size="sm"
          />
          <OurCheckbox
            size="sm"
            :label="header.title"
            :value="isHeaderVisible(header.id)"
            :disabled="header.disabled"
            :data-cy="`${dataCy}-${header.id}`"
            @input="onToggleHeader(header.id, $event)"
          />
        </div>
      </draggable>

      <div class="dropdown-actions">
        <OurButton
          :text="$t('button.cancel')"
          variant="thirdary"
          size="sm"
          :disabled="isSaving"
          :data-cy="`${dataCy}-cancel`"
          @click="onCancel"
        />
        <OurButton
          :text="$t('button.apply')"
          variant="primary"
          size="sm"
          :disabled="isSaving"
          :data-cy="`${dataCy}-apply`"
          @click="onApply"
        />
      </div>
    </div>
  </t-dropdown>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import draggable from "vuedraggable";
import TDropdown from "vue-tailwind/dist/t-dropdown";
import OurSvgIcon from "@/components/_bit/IconSvg";
import {
  buildResetTablePreferencePayload,
  buildTablePreferencePayload,
  getDefaultColumnSettings,
  mergeColumnSettings,
  normalizeColumnHeaders,
  readColumnSettingsFromPreferences,
} from "@/services/tableColumns.storage";

export default {
  name: "TableColumnsSettings",

  components: {
    draggable,
    TDropdown,
    OurSvgIcon,
  },

  props: {
    headers: {
      type: Array,
      default: () => [],
    },

    tableKey: {
      type: String,
      required: true,
    },

    preferenceSettings: {
      type: Object,
      default: () => ({}),
    },

    title: {
      type: String,
      default: "",
    },

    dataCy: {
      type: String,
      default: "table-columns",
    },
  },

  data() {
    return {
      isShownOptions: false,
      isSaving: false,
      draftSettings: getDefaultColumnSettings([]),
    };
  },

  computed: {
    ...mapGetters("user", ["tablePreferences"]),

    icons: () => ({
      Settings: require("@/components/_bit/IconSvg/icons/Settings.svg"),
      DialNumbers: require("@/components/_bit/IconSvg/icons/Dial-numbers.svg"),
    }),

    normalizedHeaders() {
      return normalizeColumnHeaders(this.headers);
    },

    headerIds() {
      return this.normalizedHeaders.map((header) => header.id).join(",");
    },

    orderedHeaders: {
      get() {
        const headersById = this.normalizedHeaders.reduce((acc, header) => {
          acc[header.id] = header;

          return acc;
        }, {});
        const ordered = this.draftSettings.order.map((id) => headersById[id]).filter(Boolean);
        const unordered = this.normalizedHeaders.filter(
          (header) => !this.draftSettings.order.includes(header.id)
        );

        return [...ordered, ...unordered];
      },

      set(nextHeaders) {
        const nextIds = nextHeaders.map((header) => header.id);
        const remaining = this.draftSettings.order.filter((id) => !nextIds.includes(id));

        this.draftSettings = {
          ...this.draftSettings,
          order: [...nextIds, ...remaining],
        };
      },
    },

    dropdownClasses() {
      return {
        button: "block",
        wrapper: "inline-flex flex-col",
        dropdownWrapper: "relative z-20 mb-0",
        dropdown: "right-0 top-0 absolute rounded-lg shadow bg-white mt-1 min-w-[14rem]",
        enterClass: "opacity-0 scale-95",
        enterActiveClass: "transition transform ease-out duration-100",
        enterToClass: "opacity-100 scale-100",
        leaveClass: "opacity-100 scale-100",
        leaveActiveClass: "transition transform ease-in duration-75",
        leaveToClass: "opacity-0 scale-95",
      };
    },
  },

  watch: {
    headerIds() {
      this.syncDraftFromStorage();
    },

    tableKey() {
      this.syncDraftFromStorage();
    },

    tablePreferences: {
      handler() {
        this.syncDraftFromStorage();
      },
      deep: true,
    },
  },

  created() {
    this.syncDraftFromStorage();
    window.addEventListener("click", this.closeOptions);
  },

  destroyed() {
    window.removeEventListener("click", this.closeOptions);
  },

  methods: {
    ...mapActions("user", ["saveTablePreference"]),

    cloneSettings(settings) {
      return {
        order: [...settings.order],
        visible: [...settings.visible],
      };
    },

    getResolvedSettings() {
      const stored = readColumnSettingsFromPreferences(this.tablePreferences, this.tableKey);

      return mergeColumnSettings(this.normalizedHeaders, stored);
    },

    syncDraftFromStorage() {
      this.draftSettings = this.cloneSettings(this.getResolvedSettings());
    },

    getAppliedIds(settings) {
      return settings.order.filter((id) => settings.visible.includes(id));
    },

    emitApplied(settings = this.getResolvedSettings()) {
      this.$emit("change", this.getAppliedIds(settings));
    },

    isHeaderVisible(id) {
      return this.draftSettings.visible.includes(id);
    },

    onToggleHeader(id, visible) {
      const header = this.normalizedHeaders.find((item) => item.id === id);

      if (header?.disabled) {
        return;
      }

      const withoutId = this.draftSettings.visible.filter((item) => item !== id);

      this.draftSettings = {
        ...this.draftSettings,
        visible: visible ? [...withoutId, id] : withoutId,
      };
    },

    async persistSettings(settings) {
      const payload = buildTablePreferencePayload(settings, this.preferenceSettings);

      await this.saveTablePreference({
        table: this.tableKey,
        columns: payload.columns,
        settings: payload.settings,
      });
    },

    async persistReset() {
      const payload = buildResetTablePreferencePayload(this.preferenceSettings);

      await this.saveTablePreference({
        table: this.tableKey,
        columns: payload.columns,
        settings: payload.settings,
      });
    },

    async onApply() {
      const settings = this.cloneSettings({
        order: this.orderedHeaders.map((header) => header.id),
        visible: this.draftSettings.visible.filter((id) =>
          this.normalizedHeaders.some((header) => header.id === id)
        ),
      });

      this.isSaving = true;

      try {
        await this.persistSettings(settings);
        this.draftSettings = settings;
        this.emitApplied(settings);
        this.isShownOptions = false;
      } finally {
        this.isSaving = false;
      }
    },

    async onCancel() {
      const defaults = getDefaultColumnSettings(this.normalizedHeaders);

      this.isSaving = true;

      try {
        await this.persistReset();
        this.draftSettings = this.cloneSettings(defaults);
        this.emitApplied(defaults);
        this.isShownOptions = false;
      } finally {
        this.isSaving = false;
      }
    },

    onMousedown() {
      if (!this.isShownOptions) {
        this.syncDraftFromStorage();
      }

      this.isShownOptions = !this.isShownOptions;
    },

    closeOptions(event) {
      if (!this.$el.contains(event.target)) {
        if (this.isShownOptions) {
          this.syncDraftFromStorage();
        }

        this.isShownOptions = false;
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.columns-settings-dropdown {
  .dropdown-content {
    @apply p-4;
  }

  .dropdown-title {
    @apply mb-3 text-sm font-medium text-gray-900;
  }

  .headers-list {
    @apply space-y-3;
  }

  .header-option-row {
    @apply flex items-center gap-2;
  }

  .dropdown-actions {
    @apply mt-4 flex gap-2;

    .button {
      @apply flex-1;
    }
  }

  .drag-icon {
    @apply shrink-0 cursor-grab;

    &:active {
      @apply cursor-grabbing;
    }
  }

  :deep(.checkbox-wrap) {
    @apply max-w-none flex-1;

    .checkbox {
      @apply mr-3;
    }

    .label {
      @apply text-sm;
    }
  }

  .ghost {
    @apply opacity-50;
  }

  .draggable-item {
    @apply bg-gray-50 py-2;
  }
}
</style>
